<template>
  <v-main class="portal-main">
    <!-- Hero Header -->
    <div class="portal-header">
      <v-container fluid>
        <v-row justify="center" align="center" class="hero-content">
          <v-col cols="12" align="center">
            <div class="hero-title">
              <img src="@/assets/img/policia.png" alt="Policía Estatal Tlaxcala" class="hero-logo mb-2" />
              <h1 class="display-1 font-weight-bold white--text mb-3">Estado de Fuerza</h1>
              <p class="subtitle-1 white--text">Portal Digital de la Secretaría de Seguridad Ciudadana</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <v-container class="mt-8">
      <v-row>
        <!-- Featured: Secretario y Video -->
        <v-col cols="12">
          <v-card class="card featured-card" elevation="8">
            <v-card-text>
              <v-container>
                <v-row>
                  <v-col cols="12" md="5" class="d-flex flex-column align-center">
                    <div class="secretary-section">
                      <v-avatar size="160" class="secretary-avatar mb-4">
                        <v-img src="@/assets/img/secretario.jpg"></v-img>
                      </v-avatar>
                      <div class="text-h5 font-weight-bold white--text mb-2">Alberto Martín Perea Marrufo</div>
                      <div class="text-subtitle-1 accent--text font-weight-medium mb-1">Capitán de Navío IM. DEM.</div>
                      <div class="text-subtitle-1 white--text font-weight-medium mb-3">
                        Secretario de Seguridad Ciudadana
                      </div>
                      <v-divider class="my-3" style="opacity: 0.3"></v-divider>
                      <div class="text-body-2 white--text quote-text">
                        <v-icon small color="accent" class="mr-2">mdi-format-quote-open</v-icon>
                        <b class="accent--text">#UnaNuevaHistoria</b>. Un gobierno cercano a la gente, sin impunidad,
                        con justicia. Un verdadero estado de derecho.
                        <v-icon small color="accent" class="ml-2">mdi-format-quote-close</v-icon>
                      </div>
                    </div>
                  </v-col>
                  <v-col cols="12" md="7">
                    <div class="video-container">
                      <div class="video-label mb-3">
                        <v-icon color="accent" class="mr-2">mdi-video</v-icon>
                        <span class="white--text font-weight-bold">Mensaje del Secretario</span>
                      </div>
                      <div class="video-wrapper">
                        <iframe
                          width="100%"
                          height="380"
                          src="https://www.youtube.com/embed/5c4Hkawga4M?si=bTAQJjzUhFHi9DGY"
                          title="YouTube video player"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerpolicy="strict-origin-when-cross-origin"
                          allowfullscreen
                        />
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Lo más reciente -->
        <v-col cols="12" md="6">
          <v-card class="card news-card" elevation="8">
            <v-card-title class="card-title-modern">
              <v-icon color="accent" class="mr-3">mdi-newspaper-variant</v-icon>
              <span>Lo más reciente</span>
            </v-card-title>
            <v-card-text>
              <v-virtual-scroll :items="feed" height="480" item-height="72">
                <template v-slot:default="{ item }">
                  <v-list-item :key="item.id" class="news-item">
                    <v-list-item-avatar>
                      <v-avatar color="accent" size="48">
                        <v-icon color="white">{{ item.icon }}</v-icon>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="white--text font-weight-medium">{{ item.titulo }}</v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-btn text color="accent" small>
                        Ver
                        <v-icon small right>mdi-arrow-right</v-icon>
                      </v-btn>
                    </v-list-item-action>
                  </v-list-item>
                  <v-divider class="mx-4" style="opacity: 0.1"></v-divider>
                </template>
              </v-virtual-scroll>
            </v-card-text>
          </v-card>

          <!-- Archivos de interés -->
          <v-card class="card files-card mt-4" elevation="8">
            <v-card-title class="card-title-modern">
              <v-icon color="accent" class="mr-3">mdi-file-document-multiple</v-icon>
              <span>Archivos de interés</span>
            </v-card-title>
            <v-card-text>
              <v-btn color="accent" block class="mb-3 file-btn" elevation="4">
                <v-icon left>mdi-file-pdf-box</v-icon>
                Convocatoria A
              </v-btn>
              <v-btn color="accent" block class="file-btn" elevation="4">
                <v-icon left>mdi-file-pdf-box</v-icon>
                Convocatoria B
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Fechas de interés -->
        <v-col cols="12" md="6">
          <v-card class="card calendar-card" elevation="8">
            <v-card-title class="card-title-modern">
              <v-icon color="accent" class="mr-3">mdi-calendar-month</v-icon>
              <span>Fechas de interés</span>
            </v-card-title>
            <v-card-text>
              <v-date-picker
                locale="es"
                color="accent"
                no-title
                full-width
                dark
                class="calendar-picker"
              ></v-date-picker>
              <v-divider class="my-4" style="opacity: 0.2"></v-divider>
              <div class="text-h6 font-weight-bold white--text mb-3">
                <v-icon color="accent" class="mr-2">mdi-bell-ring</v-icon>
                Acontecimientos
              </div>
              <v-virtual-scroll :items="acontecimientos" height="120" item-height="64">
                <template v-slot:default="{ item }">
                  <v-list-item :key="item.id" class="event-item">
                    <v-list-item-avatar>
                      <v-avatar color="accent" size="40">
                        <div class="text-subtitle-2 white--text font-weight-bold">{{ item.dia }}</div>
                      </v-avatar>
                    </v-list-item-avatar>
                    <v-list-item-content>
                      <v-list-item-title class="white--text">{{ item.titulo }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-virtual-scroll>
            </v-card-text>
          </v-card>

          <!-- Logos de Eon -->
          <div class="eon-section mt-4">
            <kinesis-container>
              <kinesis-element :strength="25" type="depth">
                <div class="eon-card">
                  <img src="@/assets/img/eon.png" alt="Eon Innovation" class="eon-logo" />
                  <p class="text-caption white--text text-center mt-2" style="opacity: 0.7">
                    Desarrollado por Eon Innovation
                  </p>
                </div>
              </kinesis-element>
            </kinesis-container>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
export default {
  data() {
    return {
      feed: [
        { id: 1, titulo: 'Fechas de asignación de tabletas a elementos', icon: 'mdi-newspaper' },
        { id: 2, titulo: 'Rutinas para sobresalir en evaluación médica', icon: 'mdi-lightbulb-on' },
        { id: 3, titulo: 'Fechas de asignación de tabletas a elementos', icon: 'mdi-newspaper' },
        { id: 4, titulo: 'Rutinas para sobresalir en evaluación médica', icon: 'mdi-lightbulb-on' },
        { id: 5, titulo: 'Fechas de asignación de tabletas a elementos', icon: 'mdi-newspaper' },
        { id: 6, titulo: 'Rutinas para sobresalir en evaluación médica', icon: 'mdi-lightbulb-on' },
        { id: 7, titulo: 'Fechas de asignación de tabletas a elementos', icon: 'mdi-newspaper' },
      ],

      acontecimientos: [
        { id: 1, titulo: 'Asignación de tabletas a elementos', dia: '02' },
        { id: 2, titulo: 'Rutinas para sobresalir en evaluación médica', dia: '24' },
      ],

      imagenes: [
        { id: 1, titulo: 'Fechas de asignación de tabletas a elementos', src: '@/assets/img/cartel1.jpg' },
        { id: 2, titulo: 'Rutinas para sobresalir en evaluación médica', src: '@/assets/img/cartel2.jpg' },
        { id: 3, titulo: 'Fechas de asignación de tabletas a elementos', src: '@/assets/img/cartel3.jpg' },
        { id: 4, titulo: 'Rutinas para sobresalir en evaluación médica', src: '@/assets/img/cartel1.jpg' },
        { id: 5, titulo: 'Fechas de asignación de tabletas a elementos', src: '@/assets/img/cartel2.jpg' },
        { id: 6, titulo: 'Rutinas para sobresalir en evaluación médica', src: '@/assets/img/cartel3.jpg' },
      ],

      pickerDate: null,
    }
  },
}
</script>

<style scoped>
/* ===========================
   MAIN LAYOUT - Tema oscuro elegante
   =========================== */
.portal-main {
  background: linear-gradient(180deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%);
  min-height: 100vh;
  padding-bottom: 40px;
}

/* ===========================
   PORTAL HEADER
   =========================== */
.portal-header {
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  padding: 60px 0 40px 0;
  position: relative;
  overflow: hidden;
  border-bottom: 2px solid rgba(187, 134, 252, 0.2);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.5);
}

/* Efectos de luz sutil con radial-gradient */
.portal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 50%, rgba(108, 92, 231, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(255, 159, 67, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-title {
  text-align: center;
  animation: fadeInUp 0.8s ease-out;
}

.hero-logo {
  width: 90px;
  height: 90px;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(187, 134, 252, 0.4));
  transition: transform 0.3s ease;
  animation: fadeInScale 0.8s ease-out;
}

.hero-logo:hover {
  transform: scale(1.05);
  filter: drop-shadow(0 8px 24px rgba(187, 134, 252, 0.6));
}

/* ===========================
   CARDS MODERNOS
   =========================== */
.card {
  background: rgba(26, 30, 46, 0.7) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(187, 134, 252, 0.2);
  border-radius: 20px;
  transition: all 0.3s ease;
  animation: fadeInUp 0.6s ease-out;
  position: relative;
  overflow: hidden;
}

.card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(187, 134, 252, 0.05) 0%, rgba(3, 218, 198, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
  border-color: rgba(187, 134, 252, 0.4);
}

.card:hover::before {
  opacity: 1;
}

/* Card title moderno */
.card-title-modern {
  background: rgba(187, 134, 252, 0.1);
  border-bottom: 1px solid rgba(187, 134, 252, 0.2);
  color: #ffffff !important;
  font-weight: 700 !important;
  padding: 20px 24px !important;
}

/* ===========================
   FEATURED CARD - Secretario
   =========================== */
.featured-card {
  border: 2px solid rgba(187, 134, 252, 0.3);
}

.secretary-section {
  text-align: center;
  padding: 20px;
}

.secretary-avatar {
  border: 5px solid rgba(187, 134, 252, 0.4);
  box-shadow: 0 8px 24px rgba(187, 134, 252, 0.4);
  transition: all 0.3s ease;
}

.secretary-avatar:hover {
  transform: scale(1.05);
  border-color: #bb86fc;
  box-shadow: 0 12px 32px rgba(187, 134, 252, 0.6);
}

.quote-text {
  background: rgba(187, 134, 252, 0.08);
  padding: 16px;
  border-radius: 12px;
  border-left: 3px solid #bb86fc;
  line-height: 1.6;
}

/* ===========================
   VIDEO CONTAINER
   =========================== */
.video-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.video-label {
  display: flex;
  align-items: center;
  font-size: 1.125rem;
}

.video-wrapper {
  flex: 1;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid rgba(187, 134, 252, 0.3);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  transition: all 0.3s ease;
}

.video-wrapper:hover {
  border-color: #bb86fc;
  box-shadow: 0 12px 32px rgba(187, 134, 252, 0.5);
  transform: scale(1.01);
}

.video-wrapper iframe {
  display: block;
  border: none;
}

/* ===========================
   NEWS CARD
   =========================== */
.news-item {
  transition: all 0.3s ease;
  border-radius: 8px;
  margin: 4px 0;
}

.news-item:hover {
  background: rgba(187, 134, 252, 0.08);
  transform: translateX(4px);
}

::v-deep .news-item .v-list-item__title {
  font-size: 0.9375rem !important;
  line-height: 1.5 !important;
}

/* ===========================
   FILES CARD
   =========================== */
.file-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  height: 48px !important;
  border-radius: 12px !important;
  letter-spacing: 0.3px;
}

.file-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(187, 134, 252, 0.4) !important;
}

/* ===========================
   CALENDAR CARD
   =========================== */
::v-deep .calendar-picker {
  background: rgba(0, 0, 0, 0.3) !important;
  border-radius: 12px;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.3);
}

::v-deep .calendar-picker .v-date-picker-table {
  height: auto !important;
}

::v-deep .calendar-picker .v-btn {
  border-radius: 8px !important;
}

.event-item {
  border-radius: 8px;
  transition: all 0.3s ease;
  margin: 4px 0;
}

.event-item:hover {
  background: rgba(187, 134, 252, 0.08);
  transform: translateX(4px);
}

/* ===========================
   EON SECTION
   =========================== */
.eon-card {
  background: rgba(26, 30, 46, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(187, 134, 252, 0.2);
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  transition: all 0.3s ease;
}

.eon-card:hover {
  transform: translateY(-4px);
  border-color: rgba(187, 134, 252, 0.4);
  box-shadow: 0 12px 32px rgba(187, 134, 252, 0.3);
}

.eon-logo {
  max-width: 70%;
  height: auto;
  border-radius: 8px;
  filter: drop-shadow(0 4px 12px rgba(187, 134, 252, 0.3));
  transition: all 0.3s ease;
}

.eon-logo:hover {
  filter: drop-shadow(0 8px 24px rgba(187, 134, 252, 0.5));
  transform: scale(1.02);
}

/* ===========================
   ACCENT COLOR
   =========================== */
::v-deep .accent--text {
  color: #bb86fc !important;
}

::v-deep .v-btn.accent {
  background: linear-gradient(135deg, #bb86fc 0%, #03dac6 100%) !important;
  color: #000000 !important;
  font-weight: 700 !important;
}

::v-deep .v-avatar.accent {
  background: linear-gradient(135deg, #bb86fc 0%, #03dac6 100%) !important;
}

/* ===========================
   SCROLLBAR
   =========================== */
::v-deep .v-virtual-scroll::-webkit-scrollbar {
  width: 8px;
}

::v-deep .v-virtual-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

::v-deep .v-virtual-scroll::-webkit-scrollbar-thumb {
  background: rgba(187, 134, 252, 0.4);
  border-radius: 4px;
  transition: background 0.3s;
}

::v-deep .v-virtual-scroll::-webkit-scrollbar-thumb:hover {
  background: rgba(187, 134, 252, 0.6);
}

/* ===========================
   ANIMACIONES
   =========================== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ===========================
   RESPONSIVE
   =========================== */
@media (max-width: 960px) {
  .portal-header {
    padding: 40px 0 30px 0;
  }

  .hero-logo {
    width: 70px;
    height: 70px;
  }

  .hero-title h1 {
    font-size: 2rem !important;
  }

  .secretary-avatar {
    width: 120px !important;
    height: 120px !important;
  }

  .video-wrapper {
    margin-top: 20px;
  }
}

@media (max-width: 600px) {
  .hero-title h1 {
    font-size: 1.5rem !important;
  }

  .hero-title p {
    font-size: 0.875rem !important;
  }

  .hero-logo {
    width: 60px;
    height: 60px;
  }

  .card-title-modern {
    font-size: 1.125rem !important;
    padding: 16px !important;
  }
}
</style>
